import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ONBOARDING_V2 } from '../../components/onboarding/onboardingV2Routes';
import { FondoCordillera } from '../../components/onboarding/marca/FondoCordillera';
import { GuiaCumbre, useDialogo, type Gesto } from '../../components/onboarding/marca/GuiaCumbre';
import { getPlatformLogo } from '../../components/PlatformLogos';
import type { Reaccion } from '../../lib/onboardingMarca/dialogo';
import { leerEstado } from '../../lib/onboardingMarca/estado';
import { enGuion } from '../../lib/onboardingMarca/guion';
import {
  avisoDeCanalesParaDespues,
  avisoDeCanalesPronto,
  CANALES,
  canalesParaAvisar,
  canalesParaDespues,
  canalParaConectar,
  dialogoDelCanal,
  esConectable,
  reaccionAlCatalogo,
  reaccionConectando,
  type CanalConectable,
} from '../../lib/onboardingMarca/canales';
import { abrirVentanaOAuth, connectTnEnVentana, getTnStatus } from '../../lib/tiendaNube';
import {
  abrirVentanaDeMercadoLibre,
  conectarMercadoLibreEnVentana,
  ErrorDeMercadoLibre,
  estadoDeMercadoLibre,
} from '../../lib/mercadoLibreConexion';
import { traerCatalogoAlConectar } from '../../lib/traerCatalogoAlConectar';
import { hayProductosDeLaTienda, revisarTiendaConectada } from '../../lib/onboardingMarca/catalogoDeLaTienda';

/**
 * Conectar el canal de venta (ADR-2026-09-29-onboarding-v2-canal-de-venta). Va justo después
 * de confirmar la marca, sólo si la persona dijo que vende por una plataforma
 * (ADR-2026-10-02-onboarding-v2-canal-despues-de-la-marca), y lo guía Cumbre.
 *
 * Tiendanube se conecta de verdad: el mismo OAuth en ventana que el resto de la app (la
 * ventana se abre SINCRÓNICA en el click, ADR-2026-08-07-tiendanube-popup-safari) y, al
 * conectar, trae el catálogo como cualquier otra puerta de Atlas
 * (`traerCatalogoAlConectar`, ADR-2026-10-01-conectar-desde-atlas-trae-el-catalogo).
 * "Conectar más adelante" sigue sin conectar: se puede hacer cuando quiera desde Mi marca.
 *
 * Mercado Libre se conecta desde el 19/10 (ADR-2026-10-19-mercadolibre-en-el-onboarding): la misma ventana
 * que Mi marca, y al conectar el BACKEND trae las publicaciones solo (acá no se relanza nada).
 * Shopify muestra el paso pero todavía no conecta: `CANALES.shopify.disponible` y un `case` en
 * `conectar` es todo lo que falta para enchufarlo.
 *
 * En modo guion (sólo DEV) no se abre ningún OAuth: la conexión y el catálogo se simulan.
 */

type Fase = 'revisando' | 'listo_para_conectar' | 'conectando' | 'conectado';
type Catalogo = 'nada' | 'trayendo' | 'listo';

function pideMenosMovimiento(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Qué gana la persona, con un ícono cada uno (los textos salen de `CANALES`). */
const ICONOS_DE_BENEFICIO = ['inventory_2', 'cloud_upload'];

export function ConectarCanal() {
  const navigate = useNavigate();
  const inicial = useMemo(() => leerEstado(null), []);
  const brandId = inicial?.brandId ?? null;
  // Con varios canales marcados, el que se puede conectar hoy (ADR-2026-10-02-onboarding-v2-preguntas-de-varias).
  const canal = canalParaConectar(inicial?.respuestas);
  // Los otros que marcó y todavía no se conectan: se le avisa que le vamos a avisar.
  const paraAvisar = canalesParaAvisar(inicial?.respuestas, canal);
  // Los otros que marcó y SÍ se conectan hoy: se le cuenta que también puede, desde Mi marca.
  const paraDespues = canalesParaDespues(inicial?.respuestas, canal);
  const info = esConectable(canal) ? CANALES[canal] : null;
  const [quieto] = useState(pideMenosMovimiento);

  const [fase, setFase] = useState<Fase>(info?.disponible ? 'revisando' : 'listo_para_conectar');
  const [tienda, setTienda] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [catalogo, setCatalogo] = useState<Catalogo>('nada');
  const [reaccion, setReaccion] = useState<Reaccion | null>(null);
  const [gesto, setGesto] = useState<Gesto | null>(null);
  // Si la persona ya se fue, lo que llega después (la conexión, el catálogo) no toca estado.
  // Se prende al montar y no sólo al crear el ref: en desarrollo (StrictMode) React monta,
  // desmonta y vuelve a montar, y con el `true` inicial quedaba apagado para siempre.
  const vivo = useRef(false);
  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  // Sin marca, o sin un canal para conectar, este paso no tiene nada que mostrar.
  useEffect(() => {
    if (!inicial?.companyId || !brandId) navigate(inicial?.companyId ? ONBOARDING_V2.setupMarca : ONBOARDING_V2.marcaSetup, { replace: true });
    else if (!info) navigate(ONBOARDING_V2.setupPiezas, { replace: true });
  }, [inicial?.companyId, brandId, info, navigate]);

  // ¿Ya estaba conectada? (vuelve de otra pestaña, o la conectó antes desde la app)
  useEffect(() => {
    if (fase !== 'revisando') return;
    if (canal === 'mercadolibre' && !enGuion()) {
      // ¿Ya la conectó (desde Mi marca, u otra pestaña)? Sus publicaciones las trae el backend al conectar.
      let activo = true;
      void estadoDeMercadoLibre(brandId).then((e) => {
        if (!activo) return;
        if (!e.conectada) return setFase('listo_para_conectar');
        setTienda(e.nickname);
        setFase('conectado');
      });
      return () => {
        activo = false;
      };
    }
    if (canal !== 'tiendanube' || enGuion()) {
      setFase('listo_para_conectar');
      return;
    }
    let activo = true;
    // Conectada de antes: si su catálogo nunca llegó a Atlas, se trae ahora (si no, en las
    // piezas no habría ningún producto para elegir). La revisión es una sola y recién después
    // se cambia la fase: cambiarla antes reiniciaba este efecto y perdía la respuesta.
    void revisarTiendaConectada(
      { companyId: inicial?.companyId, brandId },
      { estado: getTnStatus, hayProductos: hayProductosDeLaTienda },
    ).then((r) => {
      if (!activo) return;
      if (!r.conectada) return setFase('listo_para_conectar');
      setTienda(r.tienda);
      setFase('conectado');
      if (r.traerCatalogo) traerCatalogo();
    });
    return () => {
      activo = false;
    };
    // `traerCatalogo` sólo usa setters y el `brandId` de esta misma lista.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase, canal, brandId, inicial?.companyId]);

  // ── Cumbre ──
  const conectado = fase === 'conectado';
  const tramos = useMemo(
    () => (esConectable(canal) ? dialogoDelCanal({ canal, conectado, tienda }) : []),
    [canal, conectado, tienda],
  );
  const [yaHablo, setYaHablo] = useState(false);
  const dialogo = useDialogo(tramos, { instantaneo: quieto, arrancarEn: yaHablo ? 300 : 900 });
  // El contenido espera a que Cumbre termine la primera vez; después no se esconde más.
  const [mostrar, setMostrar] = useState(quieto);
  useEffect(() => {
    if (!dialogo.terminado) return;
    setMostrar(true);
    setYaHablo(true);
  }, [dialogo.terminado]);

  const seguir = () => navigate(ONBOARDING_V2.setupPiezas);

  /** Conectó en esta pantalla: festeja, trae el catálogo y Cumbre cuenta qué trajo. `traer`
   *  en false cuando se RECUPERA una conexión que ya tiene productos: relanzar el import podía
   *  duplicarlos si el anterior seguía corriendo (review #173). */
  const alConectar = (nombreDeLaTienda: string | null, traer = true) => {
    setTienda(nombreDeLaTienda);
    setFase('conectado');
    setReaccion(null);
    setGesto((g) => ({ tipo: 'festeja', n: (g?.n ?? 0) + 1 }));
    if (traer) traerCatalogo();
  };

  /** Conectó Mercado Libre: el backend ya arrancó a traer sus publicaciones (no se relanza nada). */
  const alConectarMl = (nickname: string | null) => {
    alConectar(nickname, false);
    setCatalogo('trayendo');
    setReaccion(reaccionAlCatalogo({ encontrados: 0, enviados: 0, fallo: false, masiva: true }));
  };

  /** Importa lo que la tienda tiene y Atlas no; el resultado lo dice Cumbre. */
  function traerCatalogo() {
    setCatalogo('trayendo');
    const traer = enGuion()
      ? new Promise<{ encontrados: number; enviados: number; fallo: boolean }>((r) =>
          window.setTimeout(() => r({ encontrados: 12, enviados: 12, fallo: false }), 2_500),
        )
      : traerCatalogoAlConectar(brandId);
    void traer.then((r) => {
      if (!vivo.current) return;
      const masiva = 'masiva' in r && r.masiva === true;
      // La masiva sigue en el backend: el indicador "Trayendo…" se queda mientras la persona
      // esté acá (las piezas los muestran llegar).
      setCatalogo(masiva && !r.fallo ? 'trayendo' : 'listo');
      setReaccion(reaccionAlCatalogo(r));
      if (r.enviados > 0 || masiva) setGesto((g) => ({ tipo: 'festeja', n: (g?.n ?? 0) + 1 }));
    });
  }

  /** ¿Quedó conectada aunque el aviso no llegó? Si sí, sigue como si hubiera conectado acá. */
  async function quedoConectada(): Promise<boolean> {
    if (enGuion()) return false;
    if (canal === 'mercadolibre') {
      const e = await estadoDeMercadoLibre(brandId);
      if (!e.conectada || !vivo.current) return false;
      alConectarMl(e.nickname);
      return true;
    }
    if (canal !== 'tiendanube') return false;
    const r = await revisarTiendaConectada(
      { companyId: inicial?.companyId, brandId },
      { estado: getTnStatus, hayProductos: hayProductosDeLaTienda },
    ).catch(() => ({ conectada: false as const }));
    if (!r.conectada || !vivo.current) return false;
    // Como al montar: el catálogo sólo si todavía no hay productos de la tienda.
    alConectar(r.tienda, r.traerCatalogo);
    return true;
  }

  // Volver a esta pestaña sin conectada a la vista (se conectó en la de Tiendanube y el aviso no
  // llegó, o refrescó a mitad de camino): se revisa al volver, sin otro click.
  useEffect(() => {
    if (fase !== 'listo_para_conectar') return;
    const alVolver = () => {
      if (document.visibilityState === 'visible') void quedoConectada();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
    // `quedoConectada` usa refs y valores estables de la pantalla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase]);

  /** Cada canal disponible, su conexión. Lo sincrónico (abrir la ventana) va ANTES de cualquier await. */
  const conectar = (c: CanalConectable) => {
    if (!info?.disponible || fase === 'conectando') return;
    setError(null);
    switch (c) {
      case 'tiendanube': {
        if (enGuion()) {
          setFase('conectando');
          setReaccion(reaccionConectando(c));
          window.setTimeout(() => alConectar('Tienda de prueba'), 1_500);
          return;
        }
        const popup = abrirVentanaOAuth();
        setFase('conectando');
        setReaccion(reaccionConectando(c));
        void connectTnEnVentana(brandId, popup)
          .then(() => getTnStatus(brandId))
          .then((s) => {
            if (!s.connected) throw new Error('No se pudo conectar la tienda.');
            if (vivo.current) alConectar(s.storeName);
          })
          .catch(async (e: unknown) => {
            if (!vivo.current) return;
            // Antes de decir que falló, mirar si en realidad quedó conectada: el aviso de la
            // ventana pudo perderse (celular sin opener, una pestaña que volvió tarde) o fallar
            // sólo la lectura de después (ADR-2026-10-12-tiendanube-vuelve-al-onboarding).
            if (await quedoConectada()) return;
            if (!vivo.current) return;
            const msg = e instanceof Error ? e.message : '';
            setError(msg === 'Conexión cancelada.' ? null : msg || 'No se pudo conectar la tienda. Intenta de nuevo.');
            setReaccion(null);
            setGesto((g) => ({ tipo: 'duda', n: (g?.n ?? 0) + 1 }));
            setFase('listo_para_conectar');
          });
        return;
      }
      case 'mercadolibre': {
        if (enGuion()) {
          setFase('conectando');
          setReaccion(reaccionConectando(c));
          window.setTimeout(() => alConectarMl('cuenta_de_prueba'), 1_500);
          return;
        }
        // ⚠️ La ventana se abre ACÁ, sincrónica en el click (Safari y iPhone, ADR-2026-08-07-tiendanube-popup-safari).
        const popup = abrirVentanaDeMercadoLibre();
        setFase('conectando');
        setReaccion(reaccionConectando(c));
        void conectarMercadoLibreEnVentana(brandId, popup)
          .then((r) => {
            if (vivo.current) alConectarMl(r.nickname);
          })
          .catch(async (e: unknown) => {
            if (!vivo.current) return;
            // El aviso de la ventana pudo perderse: antes de decir que falló, mirar si quedó conectada.
            if (await quedoConectada()) return;
            if (!vivo.current) return;
            const cerrada = e instanceof ErrorDeMercadoLibre && e.code === 'ventana_cerrada';
            setError(cerrada ? null : e instanceof Error && e.message ? e.message : 'No se pudo conectar. Intenta de nuevo.');
            setReaccion(null);
            setGesto((g) => ({ tipo: 'duda', n: (g?.n ?? 0) + 1 }));
            setFase('listo_para_conectar');
          });
        return;
      }
      // Shopify: todavía no (disponible: false). Su conexión va acá.
      default:
        return;
    }
  };

  if (!info || !esConectable(canal)) return null;
  const conectando = fase === 'conectando';

  return (
    <div className="relative isolate flex min-h-[calc(100dvh-var(--om-barra))] flex-col">
      <FondoCordillera quieto={quieto} />

      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-5 px-4 pb-10 pt-6 md:gap-6 md:pt-8">
        <GuiaCumbre
          tramos={tramos}
          letras={dialogo.letras}
          hablando={dialogo.hablando}
          terminado={dialogo.terminado}
          onCompletar={dialogo.completar}
          reaccion={reaccion}
          animo={conectando || catalogo === 'trayendo' ? 'piensa' : 'escucha'}
          gesto={gesto}
          instantaneo={quieto}
        />

        {mostrar && (
          <section className="om-seccion om-canal">
            <header className="flex items-center gap-3">
              <LogoDeCanal canal={canal} />
              <div className="min-w-0 flex-1">
                <h2 className="text-[17px] font-extrabold leading-tight text-brand-black">{info.nombre}</h2>
                <p className="mt-0.5 truncate text-[13px] font-semibold text-[#6B7398]">
                  {conectado
                    ? tienda
                      ? `${canal === 'mercadolibre' ? 'Cuenta' : 'Tienda'}: ${tienda}`
                      : canal === 'mercadolibre'
                        ? 'Tu cuenta'
                        : 'Tu tienda'
                    : info.disponible
                      ? 'Tu canal de venta'
                      : 'Muy pronto'}
                </p>
              </div>
              {conectado && (
                <span className="om-canal__estado">
                  <span className="material-symbols-rounded" aria-hidden="true">
                    check_circle
                  </span>
                  Conectada
                </span>
              )}
            </header>

            <ul className="flex flex-col gap-2.5">
              {info.beneficios.map((b, i) => (
                <li key={b} className={`om-canal__beneficio ${conectado ? 'is-hecho' : ''} ${info.disponible ? '' : 'is-pronto'}`}>
                  <span className="om-canal__icono material-symbols-rounded" aria-hidden="true">
                    {conectado ? 'check' : ICONOS_DE_BENEFICIO[i] ?? 'check'}
                  </span>
                  {b}
                </li>
              ))}
            </ul>

            {catalogo === 'trayendo' && (
              <div className="om-canal__trayendo" role="status">
                <span className="om-lectura__punto om-pulso" aria-hidden="true" />
                Trayendo tus productos…
              </div>
            )}
          </section>
        )}

        {/* Los otros canales que marcó: los que ya se conectan, desde Mi marca; los que todavía no
            (Shopify), que le vamos a avisar. Uno, dos o más (ADR-2026-10-19-mercadolibre-en-el-onboarding). */}
        {mostrar && paraDespues.length + paraAvisar.length > 0 && (
          <section className="om-seccion" style={{ ['--orden' as string]: 1 }} aria-labelledby="om-canal-aviso">
            <div className="om-seccion__cabeza">
              <span className="om-seccion__icono material-symbols-rounded" aria-hidden="true">
                notifications
              </span>
              <h2 id="om-canal-aviso" className="om-seccion__titulo">
                {paraDespues.length + paraAvisar.length === 1 ? 'Tu otro canal' : 'Tus otros canales'}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex shrink-0 -space-x-2">
                {[...paraDespues, ...paraAvisar].map((c) => (
                  <LogoDeCanal key={c} canal={c} chico />
                ))}
              </div>
              <p className="text-[14px] font-medium leading-snug text-[#4B5275]">
                {[
                  paraDespues.length > 0 ? avisoDeCanalesParaDespues(paraDespues) : null,
                  paraAvisar.length > 0 ? avisoDeCanalesPronto(paraAvisar, !info.disponible) : null,
                ]
                  .filter(Boolean)
                  .join(' ')}
              </p>
            </div>
          </section>
        )}
      </div>

      {mostrar && (
        <div className="om-barra">
          <div className="om-barra__dentro">
            {error && (
              <p role="alert" className="om-barra__aviso text-[#991B1B]">
                {error}
              </p>
            )}
            {info.disponible && !conectado ? (
              <>
                <button
                  type="button"
                  onClick={() => conectar(canal)}
                  disabled={conectando || fase === 'revisando'}
                  className={`om-boton md:order-last md:w-auto md:px-10 ${conectando ? 'is-enviando om-esqueleto-azul' : ''}`}
                >
                  {conectando ? 'Conectando…' : fase === 'revisando' ? 'Revisando…' : `Conectar ${info.nombre}`}
                </button>
                <div className="flex flex-col items-center gap-0.5 md:items-end">
                  {/* Nunca deshabilitado: si cancela o abandona en Tiendanube, la espera puede
                      durar hasta 10 minutos y ésta es la salida
                      (ADR-2026-10-12-tiendanube-vuelve-al-onboarding). */}
                  <button type="button" onClick={seguir} className="om-canal__despues">
                    Conectar más adelante
                  </button>
                  <span className="text-[12px] font-medium text-[#6B7398]">Puedes hacerlo cuando quieras desde Mi marca.</span>
                </div>
              </>
            ) : (
              <>
                {/* El import no depende de esta pantalla: sigue aunque la persona avance. */}
                {catalogo === 'trayendo' && <p className="om-barra__aviso">Puedes seguir: tus productos siguen llegando.</p>}
                <button type="button" onClick={seguir} className="om-boton md:w-auto md:px-10">
                  Seguir
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LogoDeCanal({ canal, chico = false }: { canal: CanalConectable; chico?: boolean }) {
  const logo = canal === 'shopify' ? null : getPlatformLogo(canal);
  return (
    <span
      className={`flex shrink-0 items-center justify-center overflow-hidden border-[1.5px] border-[rgb(var(--brand-primary-rgb)_/_0.14)] bg-white shadow-[0_10px_22px_-16px_rgb(var(--brand-primary-rgb)_/_0.55)] ${
        chico ? 'h-10 w-10 rounded-[12px]' : 'h-14 w-14 rounded-[16px]'
      }`}
    >
      {logo ? (
        <span className={`flex items-center justify-center ${chico ? 'scale-[1.4]' : 'scale-[2]'}`}>{logo}</span>
      ) : (
        <span className={`font-extrabold ${chico ? 'text-[18px]' : 'text-[24px]'}`} style={{ color: CANALES[canal].color }}>
          {CANALES[canal].nombre.slice(0, 1)}
        </span>
      )}
    </span>
  );
}

export default ConectarCanal;
